import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Link,
} from '@mui/material';
import { ArrowBack, Email } from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      setLoading(true);
      setError('');
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            underline="hover"
          >
            <ArrowBack fontSize="small" />
            Back to Sign In
          </Link>
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reset Password
        </Typography>

        {!sent ? (
          <>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoFocus
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !email.trim()}
                startIcon={<Email />}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </Box>
          </>
        ) : (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              Check your email
            </Typography>
            <Typography variant="body2">
              A password reset link has been sent to <strong>{email}</strong>. Check your inbox
              (and spam folder) and click the link to set a new password.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => { setSent(false); setEmail(''); }}
            >
              Send to a different email
            </Button>
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ForgotPasswordPage;
